import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Save, Trash2, Link2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const CATS = ["Jubiläum", "Rare", "Event", "Retro", "News", "Glitch", "Community"];
const SIZES = ["small", "medium", "large"];

const emptyEntry = { year: String(new Date().getFullYear()), title: "", text: "", badge: "", size: "medium", category: "News", date: "", order: 0 };

const TimelinePanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyEntry);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await a.listTimeline()); } finally { setLoading(false); }
  };
  useEffect(() => { load();  }, []);

  const onCreate = async () => {
    if (!form.year || !form.title.trim()) return;
    setCreating(true);
    try {
      await a.createTimeline(form);
      setForm(emptyEntry);
      toast({ title: "Eintrag angelegt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setCreating(false); }
  };

  const onSave = async (e) => {
    setSavingId(e.id);
    try {
      const payload = { year: e.year, title: e.title, text: e.text || "", badge: e.badge || "", size: e.size || "medium", category: e.category || "News", date: e.date || null, order: e.order || 0, article_id: e.article_id || null };
      await a.updateTimeline(e.id, payload);
      toast({ title: "Gespeichert" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setSavingId(null); }
  };

  const onDelete = async (e) => {
    if (!window.confirm(`Eintrag "${e.title}" wirklich löschen?`)) return;
    try { await a.deleteTimeline(e.id); toast({ title: "Gelöscht" }); await load(); }
    catch { toast({ title: "Fehler", variant: "destructive" }); }
  };

  const update = (id, field, val) =>
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, [field]: val } : x)));

  return (
    <div>
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white">Neuer Zeitleisten-Eintrag</div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[100px_1.5fr_1fr_1fr_auto]">
          <Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Jahr" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titel" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200">
            {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200">
            {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button onClick={onCreate} disabled={creating} className="btn-primary rounded-lg px-4 text-sm">
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Anlegen
          </Button>
          <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Beschreibung" rows={2} className="md:col-span-5 bg-[#10141b] border-white/10 text-zinc-200" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="mt-6 space-y-2">
          {items.map((e) => {
            const open = expanded === e.id;
            const sizeColor = e.size === "large" ? "bg-[#ff9800]" : e.size === "medium" ? "bg-[#7c4dff]" : "bg-zinc-500";
            return (
              <div key={e.id} className="rounded-xl border border-white/10 bg-[#0f141b]">
                <button onClick={() => setExpanded(open ? null : e.id)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
                  <div className="flex items-center gap-3">
                    <span className={`inline-block h-3 w-3 rounded-full ${sizeColor}`} />
                    <span className="year-pill rounded bg-white/5 px-2 py-1 text-[#ffb84d]">{e.year}</span>
                    <div>
                      <div className="text-sm font-semibold text-white flex items-center gap-2">
                        {e.title}
                        {e.article_id && <Link2 className="h-3.5 w-3.5 text-[#9d7bff]" />}
                      </div>
                      <div className="text-xs text-zinc-500">{e.category} · {e.size}</div>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-500">{open ? "Schließen" : "Bearbeiten"}</span>
                </button>
                {open && (
                  <div className="border-t border-white/10 p-4 space-y-3">
                    {e.article_id && (
                      <div className="rounded-md border border-[#7c4dff]/30 bg-[#7c4dff]/10 p-2 text-xs text-[#b39bff]">
                        Automatisch erzeugt aus News-Artikel. Änderungen am Artikel überschreiben diesen Eintrag.
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                      <Input value={e.year} onChange={(ev) => update(e.id, "year", ev.target.value)} placeholder="Jahr" className="bg-[#10141b] border-white/10 text-zinc-200" />
                      <Input value={e.title} onChange={(ev) => update(e.id, "title", ev.target.value)} placeholder="Titel" className="bg-[#10141b] border-white/10 text-zinc-200" />
                      <select value={e.category || "News"} onChange={(ev) => update(e.id, "category", ev.target.value)} className="rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200">
                        {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select value={e.size || "medium"} onChange={(ev) => update(e.id, "size", ev.target.value)} className="rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200">
                        {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <Textarea value={e.text || ""} onChange={(ev) => update(e.id, "text", ev.target.value)} rows={2} placeholder="Beschreibung" className="bg-[#10141b] border-white/10 text-zinc-200" />
                    <Input value={e.badge || ""} onChange={(ev) => update(e.id, "badge", ev.target.value)} placeholder="Badge/Bild-URL (optional)" className="bg-[#10141b] border-white/10 text-zinc-200" />
                    <div className="flex justify-end gap-2">
                      <Button onClick={() => onSave(e)} disabled={savingId === e.id} className="btn-primary rounded-lg px-3 py-1.5 text-xs">
                        {savingId === e.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                        Speichern
                      </Button>
                      <Button onClick={() => onDelete(e)} className="btn-ghost rounded-lg px-3 py-1.5 text-xs text-rose-300 hover:border-rose-500/40">
                        <Trash2 className="mr-1 h-4 w-4" /> Löschen
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TimelinePanel;
